import {FC, useEffect, useState} from 'react'
import {Building2, KeyRound, LayoutGrid, ShieldCheck} from 'lucide-react'

import * as API from '../../api'
import CompanyPermissions from './CompanyPermissions'

const PermissionMatrix: FC = () => {
  const [categories, setCategories] = useState<any[]>([])
  const [activeCategory, setActiveCategory] = useState<string | number | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  async function update() {
    setLoading(true)
    setLoadError(false)

    try {
      const response = await API.getAllCategorys()
      const data = response?.data?.data || response?.data || []
      const normalized = Array.isArray(data) ? data : []
      setCategories(normalized)
      setActiveCategory((current) => {
        if (normalized.some((category) => category.id_category === current)) return current
        return normalized[0]?.id_category ?? null
      })
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    update()
  }, [])

  const activeCategoryData = categories.find((category) => category.id_category === activeCategory)

  return (
    <section className='ua-section'>
      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <KeyRound size={20} />
          </span>
          <div>
            <h2>Permissões das empresas</h2>
            <p>Controle quais produtos, áreas e menus cada organização pode disponibilizar.</p>
          </div>
        </div>
        <span className='ua-badge ua-badge--success'>Alterações salvas automaticamente</span>
      </header>

      {loading ? (
        <div className='ua-card ua-loading'>
          <span className='ua-spinner' /> Carregando categorias…
        </div>
      ) : loadError ? (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <KeyRound size={22} />
            </span>
            <h3>Não foi possível carregar as permissões</h3>
            <p>Tente novamente para acessar as categorias e seus menus.</p>
            <button type='button' className='ua-secondary-button mt-4' onClick={update}>
              Tentar novamente
            </button>
          </div>
        </div>
      ) : categories.length ? (
        <>
          <div className='ua-stats'>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <LayoutGrid size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{categories.length}</strong>
                <small className='ua-stat__label'>Categorias de acesso</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <ShieldCheck size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>Auto</strong>
                <small className='ua-stat__label'>Persistência das alterações</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Building2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>Matriz</strong>
                <small className='ua-stat__label'>Edição por empresa</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <KeyRound size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>Massa</strong>
                <small className='ua-stat__label'>Ações por coluna ou empresa</small>
              </span>
            </div>
          </div>

          <div className='ua-card ua-category-picker'>
            <div className='ua-card__header'>
              <div>
                <h3>Categoria de acesso</h3>
                <p>Escolha uma área para visualizar e editar suas permissões.</p>
              </div>
            </div>
            <div className='ua-category-picker__list'>
              {categories.map((category) => {
                const isActive = category.id_category === activeCategory
                return (
                  <button
                    type='button'
                    key={category.id_category}
                    className={`ua-category-button ${isActive ? 'is-active' : ''}`}
                    onClick={() => setActiveCategory(category.id_category)}
                  >
                    <span>
                      <strong>{category.admin_name || category.name}</strong>
                      {category.admin_name && <small>{category.name}</small>}
                    </span>
                    <span className='ua-category-button__count'>
                      {category.products?.length || 0}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {activeCategory !== null && (
            <CompanyPermissions
              categoryId={`${activeCategory}`}
              categoryName={activeCategoryData?.admin_name || activeCategoryData?.name}
            />
          )}
        </>
      ) : (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <LayoutGrid size={22} />
            </span>
            <h3>Nenhuma categoria disponível</h3>
            <p>Cadastre categorias para montar a matriz de permissões das empresas.</p>
          </div>
        </div>
      )}
    </section>
  )
}

export default PermissionMatrix
